import { lazy, Suspense, useState } from "react";
import { Crosshair, Loader2, MapPin, ArrowDown } from "lucide-react";
import { api, formatAddress } from "../../lib/api";
import { useLang } from "../../i18n/LangContext";
import { useSettings } from "../../context/SettingsContext";
import { AddressFields } from "./AddressFields";
import { isTow, needsDestination } from "./useWizard";

const MapPicker = lazy(() => import("./MapPicker"));

const GeoButton = ({ onCoords, status }) => {
  const { t } = useLang();
  const busy = status === "locating";
  return (
    <button type="button" onClick={onCoords} disabled={busy} data-testid="wizard-use-gps-btn" className="btn-ghost w-full sm:w-auto px-5 py-3 text-sm uppercase tracking-wide">
      {busy ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : <Crosshair className="h-5 w-5 text-[#FACC15]" aria-hidden="true" />}
      {busy ? t("wizard.locating") : t("wizard.useLocation")}
    </button>
  );
};

const RouteSummary = ({ draft }) => {
  const { t } = useLang();
  const { settings } = useSettings();
  const dest = needsDestination(draft, settings) ? formatAddress(draft.destination) : "";
  const destText = dest || (draft.needs_destination_help && isTow(draft.service_id, settings) ? t("wizard.destHelpChosen") : t("wizard.noDestination"));
  return (
    <div className="panel p-5" data-testid="route-summary">
      <p className="text-xs font-bold uppercase tracking-[0.18em] text-gray-400">{t("wizard.routeSummary")}</p>
      <p className="mt-3 flex gap-2 text-white"><MapPin className="h-5 w-5 shrink-0 text-[#DC2626]" aria-hidden="true" /> <span data-testid="route-summary-pickup">{formatAddress(draft.pickup) || "—"}</span></p>
      <ArrowDown className="my-1 ml-0.5 h-4 w-4 text-gray-500" aria-hidden="true" />
      <p className="flex gap-2 text-white"><MapPin className="h-5 w-5 shrink-0 text-[#FACC15]" aria-hidden="true" /> <span data-testid="route-summary-destination">{destText}</span></p>
    </div>
  );
};

export const StepLocation = ({ draft, update, setDraft, errors }) => {
  const { t } = useLang();
  const { settings } = useSettings();
  const maps = settings.maps_enabled;
  const tow = isTow(draft.service_id, settings);
  const [geo, setGeo] = useState({ status: "idle", suggestion: null });

  const suggestFor = (lat, lng) => {
    if (!maps) return;
    api.get("/public/geocode/reverse", { params: { lat, lng } })
      .then((r) => setGeo((g) => ({ ...g, suggestion: r.data })))
      .catch(() => setGeo((g) => ({ ...g, suggestion: null })));
  };

  const locate = () => {
    if (!navigator.geolocation) {
      setGeo({ status: "unavailable", suggestion: null });
      return;
    }
    setGeo({ status: "locating", suggestion: null });
    navigator.geolocation.getCurrentPosition(
      (p) => {
        update("pickup", { lat: p.coords.latitude, lng: p.coords.longitude });
        setGeo({ status: "found", suggestion: null });
        suggestFor(p.coords.latitude, p.coords.longitude);
      },
      (err) => setGeo({ status: err.code === 1 ? "denied" : "unavailable", suggestion: null }),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  };

  const moveMarker = (lat, lng) => {
    update("pickup", { lat, lng });
    suggestFor(lat, lng);
  };

  const applySuggestion = () => {
    const s = geo.suggestion;
    update("pickup", { street: s.street || draft.pickup.street, city: s.city || draft.pickup.city, state: s.state || draft.pickup.state, zip: s.zip || draft.pickup.zip, lat: s.lat, lng: s.lng });
    setGeo((g) => ({ ...g, suggestion: null }));
  };

  const msg = { denied: "wizard.locDenied", unavailable: "wizard.locUnavailable", found: "wizard.locFound" }[geo.status];

  return (
    <div className="space-y-8">
      <fieldset className="space-y-5">
        <legend className="text-2xl sm:text-3xl font-extrabold uppercase">{t("wizard.pickup")}</legend>
        <GeoButton onCoords={locate} status={geo.status} />
        {msg && <p role="status" data-testid="geo-status-message" className={`text-sm ${geo.status === "found" ? "text-emerald-300" : "text-yellow-300"}`}>{t(msg)}</p>}
        {geo.suggestion && (
          <div className="rounded-lg border border-[#FACC15]/40 bg-[#FACC15]/5 p-4" data-testid="geo-suggestion">
            <p className="text-xs uppercase tracking-wide text-gray-400">{t("wizard.nearAddress")}</p>
            <p className="mt-1 text-sm text-white">{geo.suggestion.label}</p>
            <button type="button" onClick={applySuggestion} className="btn-yellow mt-3 px-4 py-2 text-xs" data-testid="geo-use-address-btn">{t("wizard.useThisAddress")}</button>
          </div>
        )}
        {maps && draft.pickup.lat && (
          <div className="space-y-2">
            <Suspense fallback={<div className="h-64 rounded-xl bg-[#121216]" />}>
              <MapPicker lat={draft.pickup.lat} lng={draft.pickup.lng} destLat={draft.destination.lat} destLng={draft.destination.lng} onMove={moveMarker} />
            </Suspense>
            <p className="text-xs text-gray-500">{t("wizard.dragPin")}</p>
          </div>
        )}
        {!maps && draft.pickup.lat && <p className="text-xs text-emerald-300" data-testid="coords-saved">{t("wizard.coordsSaved")}: {draft.pickup.lat.toFixed(5)}, {draft.pickup.lng.toFixed(5)}</p>}
        <AddressFields prefix="pickup" value={draft.pickup} onChange={(p) => update("pickup", p)} errors={errors} mapsEnabled={maps} notesLabel={t("wizard.pickupNotes")} notesPh={t("wizard.pickupNotesPh")} />
      </fieldset>

      <fieldset className="space-y-5 border-t border-[#2E2E3A] pt-8">
        <legend className="text-xl sm:text-2xl font-extrabold uppercase">{t("wizard.destination")}</legend>
        {tow ? (
          <label className="flex items-center gap-3 text-sm text-gray-200 cursor-pointer">
            <input type="checkbox" className="h-5 w-5 accent-[#FACC15]" checked={draft.needs_destination_help} onChange={(e) => setDraft((d) => ({ ...d, needs_destination_help: e.target.checked }))} data-testid="dest-help-checkbox" />
            {t("wizard.destHelp")}
          </label>
        ) : (
          <label className="flex items-center gap-3 text-sm text-gray-200 cursor-pointer">
            <input type="checkbox" className="h-5 w-5 accent-[#FACC15]" checked={draft.show_destination} onChange={(e) => setDraft((d) => ({ ...d, show_destination: e.target.checked }))} data-testid="dest-optional-checkbox" />
            {t("wizard.destOptional")}
          </label>
        )}
        {needsDestination(draft, settings) && (
          <div data-testid="destination-fields">
            <AddressFields prefix="destination" value={draft.destination} onChange={(p) => update("destination", p)} errors={errors} mapsEnabled={maps} notesLabel={t("wizard.destNotes")} notesPh={t("wizard.destNotesPh")} />
          </div>
        )}
      </fieldset>
      <RouteSummary draft={draft} />
    </div>
  );
};
